/**
 * Shared types for the session daemon and CLI client.
 *
 * @module
 */

/** Persisted in the pidfile at ~/.config/canary-cli/daemon.json */
export interface DaemonState {
  pid: number;
  port: number;
  startedAt: string;
}

/** Who currently controls the browser session */
export type SessionController = 'cli' | 'cloud';

/** Session-level lock — one controller at a time owns the browser */
export interface SessionLock {
  controller: SessionController;
  acquiredAt: string;
  /** If cloud was paused by a CLI force-take, the cloud can resume later */
  cloudPaused: boolean;
}

/** Session metadata returned by the daemon */
export interface SessionInfo {
  id: string;
  name: string;
  mode: 'headed' | 'headless' | 'attached';
  credentialName?: string;
  url?: string;
  startedAt: string;
  /** ISO timestamp of the last action dispatched to this session. Refreshed on tool/lock/swap calls. */
  lastActivityAt: string;
  lock: SessionLock;
  cloudRelay?: { localBrowserSessionId: string; connected: boolean };
}

/** Configuration for connecting a session to the cloud API via WebSocket relay */
export interface CloudRelayConfig {
  apiUrl: string;
  token: string;
}

/** Body for POST /sessions */
export interface CreateSessionRequest {
  headless?: boolean;
  storageStatePath?: string;
  name?: string;
  url?: string;
  credentialName?: string;
  /** When present, the daemon connects this session to the cloud API via WebSocket */
  cloudRelay?: CloudRelayConfig;
  /**
   * Attach to the user's running Chrome via chrome-devtools-mcp.
   * Mutually exclusive with headless/storageStatePath/credentialName/url/cloudRelay.
   */
  attachRealChrome?: boolean;
}

/** Standard daemon response envelope */
export interface DaemonResponse<T = unknown> {
  ok: boolean;
  data?: T;
  error?: string;
}

/** Body for POST /sessions/:id/swap-context */
export interface SwapContextRequest {
  storageStatePath?: string;
  credentialName?: string;
}

/**
 * Error code returned when the agent's action is deferred because the user is
 * actively driving the browser. Cooperative and transient: the daemon
 * auto-resumes once the user goes idle (see session/user-takeover).
 */
export const USER_PAUSED_ERROR = 'USER_PAUSED' as const;

/** Tool execution response */
export interface ToolResponse {
  ok: boolean;
  text?: string;
  images?: Array<{ data: string; mimeType: string }>;
  error?: string;
  /** Structured error code for programmatic handling */
  errorCode?: string;
}

/** Response for GET /sessions/:id/storage-state */
export interface StorageStateResponse {
  ok: boolean;
  data?: unknown;
  error?: string;
}

/* ── Captured interaction event types ──────────────────────────────── */

// Authoritative home is @chatsdet/recording; re-exported here so existing imports
// via `@/session/types` keep working.
export type { CapturedInteractionEvent, TakeoverEvent } from '@chatsdet/recording';
